import type { Result, UserInfo } from './types'

export class AbortError extends Error {
  constructor(message = 'aborted') {
    super(message)
    this.name = 'AbortError'
  }
}

export interface AuthFlowOperations<TUser extends UserInfo = UserInfo> {
  getUserInfo: () => Promise<Result<TUser>>
  auth: () => Promise<Result<TUser | null, 'abort'>>
  bindLegacyUser: () => Promise<Result<null, 'abort'>>
}

export interface AuthFlowHandlers<TUser extends UserInfo = UserInfo> {
  onBeforeGuestUpgrade?: () => Promise<void> | void
  /**
   * Awaited once the flow started from a guest and ended fully authenticated
   * (after any legacy bind) as a different user_id. Account-scoped state
   * fetched while the user was still a guest (e.g. the receipt system account
   * id) belongs to the old account and must be refetched here. Not called
   * when the guest was upgraded in place (user_id unchanged).
   */
  onAfterGuestUpgrade?: () => Promise<void> | void
  onBeforeLegacyBind?: () => Promise<void> | void
  onLegacyBindStart?: () => Promise<void> | void
  onLegacyBindEnd?: () => Promise<void> | void
  onUserInfo?: (userInfo: TUser) => Promise<void> | void
}

export type EnsureAuthenticatedUserOptions<TUser extends UserInfo = UserInfo> =
  AuthFlowOperations<TUser> & AuthFlowHandlers<TUser>

export async function ensureAuthenticatedUser<
  TUser extends UserInfo = UserInfo,
>(options: EnsureAuthenticatedUserOptions<TUser>): Promise<TUser> {
  let userInfo = await options.getUserInfo().then((result) => {
    if (!result.success) throw new Error('getUserInfo failed')
    if (!result.data) throw new Error('getUserInfo returned null data')
    return result.data
  })

  const wasGuest = userInfo.user_type === 'guest'
  const guestUserId = userInfo.user_id

  if (userInfo.user_type === 'guest') {
    await options.onBeforeGuestUpgrade?.()

    const guestAuthResult = await options.auth()
    if (!guestAuthResult.success) throw new AbortError()
    if (!guestAuthResult.data) throw new Error('auth returned null data')

    userInfo = guestAuthResult.data
  }

  if (userInfo.user_type === 'legacy') {
    await options.onBeforeLegacyBind?.()

    const bindResult = await options.bindLegacyUser()
    if (!bindResult.success) throw new AbortError()

    await options.onLegacyBindStart?.()
    while (true) {
      const legacyAuthResult = await options.auth()
      if (!legacyAuthResult.success) continue
      if (!legacyAuthResult.data) continue

      userInfo = legacyAuthResult.data
      break
    }
    await options.onLegacyBindEnd?.()
  }

  // FIXME: wait for native client to fix the issue: somehow not sending abort
  if (userInfo.user_type !== 'seekr_id') {
    throw new Error('Somehow not authenticated')
  }

  if (wasGuest && userInfo.user_id !== guestUserId) {
    await options.onAfterGuestUpgrade?.()
  }

  await options.onUserInfo?.(userInfo)

  return userInfo
}
